import { AppMessage } from '@/common/components';
import { Form, } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';

import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { DomainsApiProviders, SearchConsoleAccount, SearchConsoleAccountsFormFirstStepFields, SearchConsoleAccountsFormFirstStepInputs, SearchConsoleAccountsQueryKeys, useCreateSearchConsoleAccountsMutation } from '@/modules/DomainsApi';

import { FirstStepView } from './FirstStepView';
import { searchConsoleAccountsFormFirstStepValidationSchema } from './validations';

export const FirstStepCreateContainer: FC = memo(() => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const [form] = Form.useForm<SearchConsoleAccountsFormFirstStepFields>();
  const [formErrors, setFormErrors] =
    useState<FormFieldsErrors<typeof SearchConsoleAccountsFormFirstStepInputs>>(null);

  const { mutate: createSearchConsoleMutation, isLoading: createSearchConsoleIsLoading } =
    useCreateSearchConsoleAccountsMutation()({
      onSuccess: async (data: SearchConsoleAccount) => {
        appMessageApi.success('Search Console акаунт успішно створено!');

        await queryClient.refetchQueries([SearchConsoleAccountsQueryKeys.GetAll], { exact: false });

        navigate(AppRoutes.DomainsApiSearchConsoleAccountsCreateCreds.replace(':searchConsoleAccountId', data.id));
      },
      onError: (err) => {
        setFormErrors(extractFormErrors<typeof SearchConsoleAccountsFormFirstStepInputs>(err));
        messageOnError(err, appMessageApi);
      }
    });

  const initialValues: SearchConsoleAccountsFormFirstStepFields = {
    email: undefined,
    password: undefined,
    provider: DomainsApiProviders.SearchConsole,
    backupEmailAddress: undefined
  };

  const handleFinish = useCallback((values: SearchConsoleAccountsFormFirstStepFields): void => {
    setFormErrors(null);
    createSearchConsoleMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.DomainsApiSearchConsoleAccounts);
  }, []);

  return (
    <FirstStepView
      formName={ 'create-search-console-account-form-first-step' }
      formType={ 'create' }
      form={ form }
      initialValues={ initialValues }
      formErrors={ formErrors }
      isFormLoading={ createSearchConsoleIsLoading }
      validationSchema={ searchConsoleAccountsFormFirstStepValidationSchema }
      handleBack={ handleBack }
      handleFinish={ handleFinish }
      handleFinishFailed={ handleFinishFailed }
    />
  );
});
